import { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toAbsoluteAssetUrl } from "@/lib/api-client";
import type { ImageFolderPhoto } from "@shared/api";
import { useIsMobile } from "@/hooks/use-mobile";

interface PhotoViewerProps {
  photos: ImageFolderPhoto[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
  /** Optional label shown under the image (e.g. "Garážová brána 1-1") */
  getPhotoLabel?: (photo: ImageFolderPhoto) => string;
  /** When set, shows "Open in folder" button that calls this with the current photo's folder_id */
  onOpenInFolder?: (folderId: string) => void;
  /** Label for the open-in-folder button (e.g. from i18n) */
  openInFolderLabel?: string;
}

export default function PhotoViewer({
  photos,
  currentIndex,
  onClose,
  onNavigate,
  getPhotoLabel,
  onOpenInFolder,
  openInFolderLabel,
}: PhotoViewerProps) {
  const isMobile = useIsMobile();
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const currentPhoto = photos[currentIndex];
  const currentLabel = currentPhoto && getPhotoLabel ? getPhotoLabel(currentPhoto) : "";
  const folderId = currentPhoto?.folder_id;

  const goPrev = useCallback(() => {
    onNavigate(currentIndex > 0 ? currentIndex - 1 : photos.length - 1);
  }, [currentIndex, photos.length, onNavigate]);
  const goNext = useCallback(() => {
    onNavigate(currentIndex < photos.length - 1 ? currentIndex + 1 : 0);
  }, [currentIndex, photos.length, onNavigate]);

  useEffect(() => {
    if (currentIndex === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
      else if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, goPrev, goNext, onClose]);

  if (!currentPhoto) return null;

  if (typeof document === "undefined") return null;

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const SWIPE_THRESHOLD = 50;
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart.current == null) return;
    const dx = e.touches[0].clientX - touchStart.current.x;
    const dy = e.touches[0].clientY - touchStart.current.y;
    if (Math.abs(dx) > 20 || Math.abs(dy) > 20) e.preventDefault();
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current == null) return;
    const x = e.changedTouches[0].clientX;
    const y = e.changedTouches[0].clientY;
    const deltaX = x - touchStart.current.x;
    const deltaY = y - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(deltaY) > SWIPE_THRESHOLD && deltaY > Math.abs(deltaX)) {
      onClose();
      return;
    }
    if (photos.length <= 1) return;
    if (deltaX > SWIPE_THRESHOLD) goPrev();
    else if (deltaX < -SWIPE_THRESHOLD) goNext();
  };

  const arrowBtnClass = isMobile
    ? "absolute top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 shadow-lg border border-white/20"
    : "absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20";
  const arrowNextClass = isMobile
    ? "absolute top-1/2 -translate-y-1/2 text-white bg-black/50 hover:bg-black/70 rounded-full p-3 shadow-lg border border-white/20"
    : "absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20";

  const overlay = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur"
      onClick={handleBackgroundClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: "pan-y" }}
      role="presentation"
    >
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          // safe-area: top inset (portal ensures above Layout header z-50)
          "absolute z-20 text-white top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))]",
          isMobile ? "bg-black/50 hover:bg-black/70 rounded-full p-2 shadow-lg border border-white/20" : "hover:bg-white/20"
        )}
        onClick={onClose}
        aria-label="Bezárás"
      >
        <X className="h-6 w-6" />
      </Button>

      {photos.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className={cn("left-4 z-[2]", arrowBtnClass)}
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
        >
          <ChevronLeft className={isMobile ? "h-10 w-10" : "h-8 w-8"} />
        </Button>
      )}

      {/* Image and caption (counter + label egymás fölött) */}
      <div
        className="flex flex-col items-center gap-3 px-4 py-6 md:py-10 md:px-6"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={toAbsoluteAssetUrl(currentPhoto.file_url)}
          alt={currentPhoto.file_name}
          className="max-h-[min(90vh,calc(100dvh-8rem))] max-w-[90vw] object-contain md:max-h-[min(85vh,calc(100dvh-10rem))]"
        />
        {(() => {
          const parts = [
            photos.length > 1 ? `${currentIndex + 1}/${photos.length}` : null,
            currentLabel || null,
          ].filter(Boolean) as string[];
          const caption = parts.join(" – ");
          return caption ? (
            <div className="text-white text-sm bg-black/50 px-4 py-2 rounded">{caption}</div>
          ) : null;
        })()}
        {folderId && onOpenInFolder && (
          <Button
            variant="secondary"
            size="sm"
            className="mt-2 gap-2"
            onClick={() => onOpenInFolder(folderId)}
          >
            <FolderOpen className="h-4 w-4" />
            {openInFolderLabel ?? "Megnyitás mappában"}
          </Button>
        )}
      </div>

      {/* Next button */}
      {photos.length > 1 && (
        <Button
          variant="ghost"
          size="icon"
          className={cn("right-4 z-[2]", arrowNextClass)}
          onClick={(e) => { e.stopPropagation(); goNext(); }}
        >
          <ChevronRight className={isMobile ? "h-10 w-10" : "h-8 w-8"} />
        </Button>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
